"use client";

import { IFormInput, IFormProps, IFormSubmit } from "@template/components/Form";
import React, { useState } from "react";
import { Form } from "@template/components";
import TabContent from "@template/components/Tabs/TabContent";
import { TabsSectionContainer } from "@template/components/Tabs/styles.ts";
import * as S from "./styles.ts";
import axios from "axios";
import { useTypedDispatch } from "@/redux/helpers/hooks.ts";
import { getCart } from "@/redux/slices/user/thunks/getCart.ts";
import ArchStandaloneSectionComponent from "@template/blocks/ArchStandaloneSection/component";

type DomainNameSearchBulkProps = {
  input?: Omit<IFormInput, "name">;
  fileInput?: Omit<IFormInput, "name">;
  submit?: Omit<IFormSubmit, "onSubmit">;
  arch?: boolean;
};

const DomainNameSearchTransfer = ({
                                    input,
                                    fileInput,
                                    submit,
                                    arch = true,
                                  }: DomainNameSearchBulkProps) => {
  const dispatch = useTypedDispatch();
  const [formValue, setFormValue] = useState<{
    search: string;
    file: null | File[];
  }>({
    search: "",
    file: null,
  });
  const [fileError, setFileError] = useState<false | string>(false);
  const [loading, setLoading] = useState(false);

  const inputs: IFormProps["inputs"] = [
    {
      ...input,
      type: "textarea",
      name: "search",
    },
    {
      divider: true,
      text: "Or",
    },
    {
      ...fileInput,
      error: fileError,
      type: "file",
      name: "file",
    },
  ];

  const handleChange = (vals: any) => {
    if ((fileError && !vals.file) || vals.file !== formValue.file) {
      setFileError(false);
    }
    setFormValue(vals);
  };

  const handleTransfer = (
    domains: {
      name: string;
      auth_code?: string;
    }[]
  ) => {
    if (!domains.length) return;
    setLoading(true);
    axios
      .post("/api/domain-names/transfer", {
        domains,
      })
      .then((r) => {
        const data = r.data;
        if (data?.added_to_cart?.length) {
          dispatch(getCart());
        }
        setLoading(false);
        if (data?.not_added_to_cart?.length) {
          setFileError("Some domains could not be added to cart");
        }
      });
  };

  const onSubmit = (values: any) => {
    const { search, file } = values;
    if (file?.size) {
      try {
        const reader = new FileReader();

        reader.onload = (e) => {
          const fileContent = e.target?.result;
          if (!fileContent) {
            throw new Error("Error reading file");
          }
          const formattedFileContent = fileContent
            .toString()
            .split("\n")
            .map((line: string) => {
              const [name, auth_code] = line.split(",");
              return {
                name: name.replace("\r", ""),
                auth_code: auth_code.replace("\r", "") || undefined,
              };
            });

          handleTransfer(formattedFileContent);
        };

        reader.onerror = (e) => {
          throw new Error("Error reading file");
        };

        reader.readAsText(file);
      } catch (e) {
        setFileError("Error reading file");
        return;
      }
    } else {
      if (!search) {
        setFileError("Please enter a domain name");
        return;
      }
      const formattedSearch = search
        .split("\n")
        .map((line: string) => {
          const split = line.split(",");
          return {
            name: split[0],
            auth_code: split[1],
          };
        })
        .filter((line: { name: string; auth_code: string }) => line.name);
      handleTransfer(formattedSearch);
    }
  };

  return (
    <S.Wrapper>
      <ArchStandaloneSectionComponent
        color="white"
        background="dark"
        withPadding
      />
      <TabsSectionContainer
        style={{
          zIndex: 10,
        }}
      >
        <TabContent>
          <Form
            value={formValue}
            onChange={handleChange}
            inputs={inputs}
            simple
            submit={{
              ...submit,
              text: submit.children,
              style: {
                width: "fit-content",
                marginLeft: "auto",
              },
              loading: loading,
              onSubmit,
            }}
          />
        </TabContent>
      </TabsSectionContainer>
    </S.Wrapper>
  );
};

export default DomainNameSearchTransfer;
